/**
 * Reclaim - Living Vision Board V2
 * Premium Subconscious Conditioning Experience
 * 
 * This is NOT a vision board tool. 
 * It is a daily subconscious installation system.
 */

import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate, useLocation } from 'react-router-dom';
import { useResolvedUserId } from '../../hooks/useResolvedUserId';
import { 
  ArrowLeft, 
  ArrowRight, 
  Plus, 
  Lock,
  Upload,
  X,
  Check,
  Play,
  Bell,
  Sparkles,
  Volume2,
  VolumeX,
  Pause,
  Heart,
  Eye,
  Settings,
  Film,
  Image as ImageIcon
} from 'lucide-react';
import Particles, { initParticlesEngine } from "@tsparticles/react";
import { loadSlim } from "@tsparticles/slim";
import RevealScreen from './RevealScreen';
import VideoImmersionScreen from './VideoImmersionScreen';
import useFoundationVideo from '../../hooks/useFoundationVideo';
import { useAuth } from '../../hooks/useAuth';
import ModeSelector from '../../components/audio/ModeSelector';
import AudioEntry from '../../components/audio/AudioEntry';
import useAudioMode from '../../hooks/useAudioMode';
import useImagePersonalisation from '../../hooks/useImagePersonalisation';
import { trackImmersionPlayed } from '../../lib/dailyEssentialsTracker';

// Inline mode bar — appears once user has selected enough affirmations
const VisionBoardModeBar = () => {
  const [mode, setMode] = useAudioMode();
  return (
    <div className="mt-4 mb-2 rounded-2xl py-2" style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.05)' }}>
      <ModeSelector selected={mode} onChange={setMode} compact />
    </div>
  );
};

import {
  CATEGORIES,
  CATEGORY_IMAGES,
  IMMERSION_SCENES,
  IDENTITY_WORDS,
  IMMERSION_AFFIRMATIONS,
  AFFIRMATIONS,
  SUBLIMINAL_CONFIG,
} from './livingVisionBoardData';


// ============================================
// STEP COMPONENTS
// ============================================

// Step 0: Entry Screen — Absorption aesthetic
// ─────────────────────────────────────────────────────────────────────
// ImageSourceChoice — user's first creative decision on Living Vision.
// Asked for by a Reclaim user (May 2026) who wanted to use her own
// photographs as vision anchors rather than Reclaim's curated library.
//
// Two choices, same ritual aesthetic as EntryScreen:
//   1. Select from my own files  (user-provided symbols)
//   2. Select from Reclaim files (curated library — existing flow)
// The smaller-font clarifier under the second option ("images are
// symbols of what you would like to bring in your own reality") is
// copy-for-copy the user's request.
// ─────────────────────────────────────────────────────────────────────
const ImageSourceChoice = ({ onBack, onChooseReclaim, onChooseOwn }) => {
  return (
    <motion.div
      className="fixed inset-0 flex flex-col items-center justify-center px-6"
      style={{ background: "#060a07", zIndex: 30 }}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.7 }}
      data-testid="living-vision-source-choice"
    >
      <button
        onClick={onBack}
        aria-label="Back"
        className="absolute top-6 left-6 p-2 rounded-full hover:bg-white/5 transition-colors"
        data-testid="vision-source-back"
      >
        <span className="text-white/60 text-sm">← Back</span>
      </button>

      <motion.h2
        className="text-white/80 text-center text-3xl md:text-4xl mb-4"
        style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontWeight: 300 }}
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.2, duration: 0.9 }}
      >
        Where should your vision begin?
      </motion.h2>
      <motion.p
        className="text-white/30 text-center text-xs tracking-[0.3em] uppercase mb-16"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.5, duration: 0.8 }}
      >
        Two paths. Same destination.
      </motion.p>

      <div className="flex flex-col gap-4 w-full max-w-sm">
        {/* OPTION 1 — own files */}
        <motion.button
          onClick={onChooseOwn}
          className="w-full px-8 py-6 rounded-3xl text-left cursor-pointer"
          style={{
            background: "rgba(255,255,255,0.05)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(255,255,255,0.1)",
            boxShadow: "0 8px 28px rgba(0,0,0,0.2)",
          }}
          whileHover={{ background: "rgba(255,255,255,0.09)", scale: 1.015 }}
          whileTap={{ scale: 0.98 }}
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.9, duration: 0.8 }}
          data-testid="vision-source-own-files"
        >
          <p
            className="text-white/88 text-lg mb-1"
            style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic" }}
          >
            Select from my own files
          </p>
          <p className="text-white/45 text-xs" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Your photographs, your symbols
          </p>
        </motion.button>

        {/* OPTION 2 — Reclaim files */}
        <motion.button
          onClick={onChooseReclaim}
          className="w-full px-8 py-6 rounded-3xl text-left cursor-pointer"
          style={{
            background: "rgba(200,180,120,0.08)",
            backdropFilter: "blur(20px)",
            border: "1px solid rgba(200,180,120,0.25)",
            boxShadow: "0 8px 28px rgba(200,180,120,0.12)",
          }}
          whileHover={{ background: "rgba(200,180,120,0.14)", scale: 1.015 }}
          whileTap={{ scale: 0.98 }}
          initial={{ opacity: 0, y: 18 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.05, duration: 0.8 }}
          data-testid="vision-source-reclaim-files"
        >
          <p
            className="text-white/92 text-lg mb-1"
            style={{ fontFamily: "'Playfair Display', serif", fontStyle: "italic" }}
          >
            Select from Reclaim files
          </p>
          <p
            className="text-white/50 text-[11px] leading-relaxed"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            Images are symbols of what you would like to bring into your reality
          </p>
        </motion.button>
      </div>
    </motion.div>
  );
};


const EntryScreen = ({ onStart, onCreatePerformance, savedVisions = [] }) => {  const bgUrl = 'https://images.pexels.com/photos/23499343/pexels-photo-23499343.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';
  const navigate = useNavigate();
  const [showAdminMenu, setShowAdminMenu] = useState(false);
  const { user } = useAuth();
  const isAdmin = user?.role === 'admin';

  return (
    <motion.div 
      className="fixed inset-0 flex flex-col overflow-y-auto"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Moving ocean/sky background — slow breathing scale */}
      <motion.div 
        className="fixed inset-0 z-0"
        animate={{ scale: [1, 1.04, 1] }}
        transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut' }}
      >
        <img src={bgUrl} alt="" className="w-full h-full object-cover" style={{ filter: 'brightness(0.8) saturate(0.85)' }} />
      </motion.div>
      <div className="fixed inset-0 z-0" style={{
        background: 'linear-gradient(180deg, rgba(8,12,16,0.2) 0%, rgba(8,12,16,0.45) 40%, rgba(8,12,16,0.82) 70%, rgba(8,12,16,0.96) 100%)',
      }} />
      
      {/* Warm ambient glow — golden hour pulse */}
      <motion.div
        className="fixed inset-0 pointer-events-none z-[1]"
        animate={{
          background: [
            'radial-gradient(ellipse 90% 70% at 50% 30%, rgba(200,170,100,0.05) 0%, transparent 55%)',
            'radial-gradient(ellipse 100% 80% at 55% 35%, rgba(180,190,120,0.07) 0%, transparent 55%)',
            'radial-gradient(ellipse 90% 70% at 50% 30%, rgba(200,170,100,0.05) 0%, transparent 55%)',
          ],
        }}
        transition={{ duration: 14, repeat: Infinity, ease: 'easeInOut' }}
      />

      {/* Admin settings — top right, only for admins */}
      {isAdmin && (
        <div className="fixed top-5 right-5 z-50">
          <motion.button
            onClick={() => setShowAdminMenu(v => !v)}
            className="p-2.5 rounded-full transition-all"
            style={{
              background: 'rgba(255,255,255,0.06)',
              backdropFilter: 'blur(12px)',
              border: '1px solid rgba(255,255,255,0.08)',
            }}
            whileTap={{ scale: 0.92 }}
            data-testid="admin-settings-btn"
          >
            <Settings size={16} className="text-white/30" />
          </motion.button>

          <AnimatePresence>
            {showAdminMenu && (
              <motion.div
                className="absolute top-12 right-0 w-52 rounded-xl overflow-hidden"
                style={{
                  background: 'rgba(15,18,22,0.92)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255,255,255,0.08)',
                  boxShadow: '0 12px 40px rgba(0,0,0,0.4)',
                }}
                initial={{ opacity: 0, y: -8, scale: 0.95 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: -8, scale: 0.95 }}
                transition={{ duration: 0.2 }}
              >
                <button
                  onClick={() => navigate('/abundance/admin/images')}
                  className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-white/5 transition-colors"
                  data-testid="admin-manage-images-btn"
                >
                  <ImageIcon size={14} className="text-white/40" />
                  <span className="text-white/65 text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>Manage Images</span>
                </button>
                <div className="mx-4 h-px bg-white/5" />
                <button
                  onClick={() => navigate('/abundance/admin/videos')}
                  className="w-full flex items-center gap-3 px-4 py-3.5 text-left hover:bg-white/5 transition-colors"
                  data-testid="admin-manage-videos-btn"
                >
                  <Film size={14} className="text-white/40" />
                  <span className="text-white/65 text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>Manage Videos</span>
                </button>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      )}

      {/* Content */}
      <div className="relative z-10 flex flex-col items-center justify-center min-h-full pt-16 pb-12 px-8">
        {/* Emotional copy — slow reveal */}
        <motion.div 
          className="text-center mb-14 max-w-md" 
          initial={{ opacity: 0, y: 30 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ delay: 0.5, duration: 1.2 }}
        >
          <motion.p 
            className="text-white/40 text-sm md:text-base mb-8 leading-loose"
            style={{ fontFamily: "'Manrope', sans-serif", letterSpacing: '0.04em' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.8, duration: 1.5 }}
          >
            This is not about creating a vision.<br />
            This is about <span className="text-[#E8D8C8]/60">becoming it</span>.
          </motion.p>

          <motion.h1 
            className="text-4xl md:text-5xl text-[#FAF9F6]/90 mb-6 leading-tight"
            style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400, fontStyle: 'italic' }}
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1.4, duration: 1 }}
          >
            Your Living Vision
          </motion.h1>

          <motion.p 
            className="text-white/30 text-sm leading-relaxed"
            style={{ fontFamily: "'Manrope', sans-serif", letterSpacing: '0.03em' }}
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 2, duration: 1 }}
          >
            You don't need to force anything.<br />
            Just enter… and let it install.
          </motion.p>
        </motion.div>

        {/* Saved Visions — if any */}
        {savedVisions.length > 0 && (
          <motion.div 
            className="w-full max-w-lg mb-10" 
            initial={{ opacity: 0, y: 20 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ delay: 2.4, duration: 0.8 }}
          >
            <p className="text-white/20 text-xs mb-3 text-center tracking-widest uppercase" style={{ fontFamily: "'Manrope', sans-serif" }}>Your Visions</p>
            <div className="flex gap-4 overflow-x-auto pb-2 scrollbar-hide px-2 justify-center">
              {savedVisions.map((vision, i) => (
                <motion.div 
                  key={vision.id || i} 
                  className="flex-shrink-0 w-36 h-28 rounded-xl overflow-hidden cursor-pointer" 
                  style={{ boxShadow: '0 8px 24px rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.06)' }} 
                  whileHover={{ scale: 1.05 }} 
                  whileTap={{ scale: 0.97 }} 
                  onClick={() => navigate('/abundance/my-visions')}
                >
                  {vision.selected_images && vision.selected_images[0] ? (
                    <img src={vision.selected_images[0].url} alt="" className="w-full h-full object-cover" style={{ filter: 'brightness(0.85) saturate(0.9)' }} />
                  ) : (
                    <div className="w-full h-full bg-white/5 flex items-center justify-center"><span className="text-white/20 text-xs">{vision.name || 'Vision'}</span></div>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.div>
        )}

        {/* CTA — "Enter Your Vision" */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }} 
          animate={{ opacity: 1, y: 0 }} 
          transition={{ delay: 2.8, duration: 0.8 }} 
          className="w-full max-w-sm"
        >
          <motion.button 
            onClick={onStart} 
            className="w-full px-12 py-5 rounded-full text-lg font-medium text-white/85 cursor-pointer" 
            style={{ 
              fontFamily: "'Playfair Display', serif", 
              fontStyle: 'italic',
              background: 'rgba(255,255,255,0.06)', 
              backdropFilter: 'blur(20px)', 
              border: '1px solid rgba(255,255,255,0.12)', 
              boxShadow: '0 8px 32px rgba(0,0,0,0.15)', 
              letterSpacing: '0.06em' 
            }} 
            whileHover={{ background: 'rgba(255,255,255,0.12)', boxShadow: '0 12px 40px rgba(0,0,0,0.2)' }} 
            whileTap={{ scale: 0.98 }} 
            data-testid="start-creating-btn"
          >
            <motion.span
              animate={{
                textShadow: [
                  '0 0 20px rgba(200,180,120,0.1)',
                  '0 0 40px rgba(200,180,120,0.2)',
                  '0 0 20px rgba(200,180,120,0.1)',
                ],
              }}
              transition={{ duration: 5, repeat: Infinity, ease: 'easeInOut' }}
            >
              Enter Your Vision
            </motion.span>
          </motion.button>

          {/* Living Creation Engine — generate personalised performance images */}
          <motion.button
            onClick={onCreatePerformance}
            className="w-full mt-3 px-8 py-3 rounded-full text-xs uppercase tracking-[0.32em] cursor-pointer"
            style={{
              fontFamily: "'Manrope', sans-serif",
              background: 'transparent',
              border: '1px solid rgba(203,161,83,0.32)',
              color: 'rgba(203,161,83,0.85)',
            }}
            whileHover={{ background: 'rgba(203,161,83,0.06)', borderColor: 'rgba(203,161,83,0.5)' }}
            whileTap={{ scale: 0.98 }}
            data-testid="create-performance-vision-btn"
          >
            ✦ Create my performance vision
          </motion.button>
        </motion.div>

        {/* Footer — anchoring copy */}
        <motion.p 
          className="text-white/18 text-sm mt-12 text-center leading-relaxed" 
          style={{ fontFamily: "'Manrope', sans-serif" }} 
          initial={{ opacity: 0 }} 
          animate={{ opacity: 1 }} 
          transition={{ delay: 3.2, duration: 0.8 }}
        >
          No pressure. Only truth.
        </motion.p>
      </div>
    </motion.div>
  );
};

// Category Progress Bar — glass, minimal
const CategoryProgressBar = ({ categories, currentIndex }) => {
  return (
    <div className="fixed top-0 left-0 right-0 z-40 py-3 px-4" style={{ background: 'rgba(10,15,20,0.6)', backdropFilter: 'blur(16px)', borderBottom: '1px solid rgba(255,255,255,0.04)' }}>
      <div className="flex items-center justify-center gap-1 overflow-x-auto scrollbar-hide">
        {categories.map((cat, index) => (
          <div 
            key={cat.id}
            className="flex items-center gap-1 px-3 py-1.5 rounded-full text-xs transition-all duration-300 whitespace-nowrap"
            style={{
              fontFamily: "'Manrope', sans-serif",
              background: index === currentIndex ? 'rgba(255,255,255,0.08)' : 'transparent',
              border: index === currentIndex ? '1px solid rgba(255,255,255,0.12)' : '1px solid transparent',
              color: index === currentIndex ? 'rgba(250,249,246,0.9)' : index < currentIndex ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.2)',
            }}
          >
            <span>{cat.icon}</span>
            <span className="hidden md:inline">{cat.name}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

// Image Selection Screen (Pinterest-style masonry)
const ImageSelectionScreen = ({ 
  category, 
  images, 
  selectedImages, 
  onSelect, 
  onNext, 
  onBack,
  totalSelected,
  categoryIndex,
  totalCategories,
  favorites,
  onToggleFavorite,
  isFavorited,
  personalised = false
}) => {
  const [expandedImage, setExpandedImage] = useState(null);
  const categorySelected = selectedImages.filter(img => img.category === category.id).length;
  const recommended = 3;
  
  // Sort images: favorites first, then preserve incoming order (which is
  // already personalised by tone profile when `personalised` is true). If
  // not personalised, hero images bubble up next.
  const sortedImages = useMemo(() => {
    return [...images].sort((a, b) => {
      const aFav = isFavorited(a.url) ? 1 : 0;
      const bFav = isFavorited(b.url) ? 1 : 0;
      if (aFav !== bFav) return bFav - aFav;
      if (personalised) return 0; // preserve incoming personalised order
      const aHero = a.hero ? 1 : 0;
      const bHero = b.hero ? 1 : 0;
      return bHero - aHero;
    });
  }, [images, isFavorited, personalised]);

  return (
    <motion.div 
      className="fixed inset-0 flex flex-col pt-16"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Cinematic bg */}
      <div className="fixed inset-0 z-0">
        <img src="https://images.pexels.com/photos/23499343/pexels-photo-23499343.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover" style={{ filter: 'brightness(0.5) saturate(0.7)' }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(10,15,20,0.6) 0%, rgba(10,15,20,0.85) 40%, rgba(10,15,20,0.95) 100%)' }} />
      </div>

      {/* Header */}
      <div className="relative z-10 flex-shrink-0 px-6 pt-4 pb-4">
        <div className="flex items-center justify-between mb-4">
          <button 
            onClick={onBack}
            className="p-2 rounded-full transition-colors"
            style={{ background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.08)' }}
            data-testid="back-btn"
          >
            <ArrowLeft size={20} className="text-white/60" />
          </button>
          
          <div className="text-center">
            <span className="text-2xl mb-1 block">{category.icon}</span>
            <h2 
              className="text-[#FAF9F6] text-xl"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              {category.name}
            </h2>
            {personalised && (
              <span
                className="inline-block mt-1 text-[10px] tracking-[0.2em] uppercase"
                style={{ color: 'rgba(203,161,83,0.85)', fontFamily: "'Manrope', sans-serif" }}
                data-testid="personalisation-badge"
              >
                ✦ tuned to you
              </span>
            )}
          </div>

          <div 
            className="px-3 py-1.5 rounded-full"
            style={{ background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.08)', fontFamily: "'Manrope', sans-serif" }}
          >
            <span className="text-white/60 text-sm font-medium">{totalSelected} / 50</span>
          </div>
        </div>

        <p className="text-center text-white/40 text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>
          {categorySelected < recommended ? (
            <>Select at least {recommended - categorySelected} more</>
          ) : (
            <span className="text-white/60">Great choices</span>
          )}
        </p>
      </div>

      {/* Masonry Grid */}
      <div className="flex-1 overflow-y-auto px-4 pb-32">
        <div className="columns-2 md:columns-3 gap-3 space-y-3">
          {sortedImages.map((image, index) => {
            const isSelected = selectedImages.some(img => img.url === image.url);
            const isFav = isFavorited(image.url);
            return (
              <motion.div
                key={image.url}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(index * 0.03, 0.5) }}
                className={`break-inside-avoid relative rounded-2xl overflow-hidden cursor-pointer transition-all duration-500 ${
                  isSelected ? 'ring-2 ring-white/20 scale-[1.02]' : 'hover:scale-[1.02]'
                }`}
                style={{ 
                  boxShadow: isSelected ? '0 0 30px rgba(74, 103, 65, 0.3)' : 'none'
                }}
                onClick={() => onSelect(image)}
                onContextMenu={(e) => { e.preventDefault(); setExpandedImage(image); }}
                data-testid={`image-${index}`}
              >
                <img 
                  src={image.url} 
                  alt=""
                  className="w-full h-auto object-cover"
                  loading="lazy"
                />
                
                {/* Heart/Favorite Button */}
                <motion.button
                  onClick={(e) => {
                    e.stopPropagation();
                    onToggleFavorite(image, category.id);
                  }}
                  className={`absolute top-3 left-3 w-8 h-8 rounded-full flex items-center justify-center transition-all duration-300 ${
                    isFav 
                      ? 'bg-rose-500/90 shadow-lg' 
                      : 'bg-black/30 backdrop-blur-sm hover:bg-black/50'
                  }`}
                  whileHover={{ scale: 1.1 }}
                  whileTap={{ scale: 0.9 }}
                  data-testid={`favorite-${index}`}
                >
                  <Heart 
                    size={14} 
                    className={isFav ? 'text-white fill-white' : 'text-white/70'}
                  />
                </motion.button>
                
                {isSelected && (
                  <motion.div 
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    className="absolute top-3 right-3 w-8 h-8 rounded-full flex items-center justify-center"
                    style={{ background: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(8px)', border: '1px solid rgba(255,255,255,0.2)' }}
                  >
                    <Check size={16} className="text-white" />
                  </motion.div>
                )}

                {!isSelected && (
                  <div className="absolute inset-0 bg-black/20 hover:bg-transparent transition-colors pointer-events-none" />
                )}
                
                {/* Favorite indicator glow */}
                {isFav && (
                  <div 
                    className="absolute inset-0 pointer-events-none"
                    style={{
                      boxShadow: 'inset 0 0 30px rgba(244, 63, 94, 0.15)',
                      borderRadius: 'inherit'
                    }}
                  />
                )}
              </motion.div>
            );
          })}
        </div>
      </div>

      {/* Fixed Bottom CTA */}
      <div className="fixed bottom-0 left-0 right-0 p-6 pb-8 z-20" style={{ background: 'linear-gradient(to top, rgba(10,15,20,0.95) 0%, rgba(10,15,20,0.8) 60%, transparent 100%)' }}>
        {(() => {
          const isLast = categoryIndex >= totalCategories - 1;
          const capHit = totalSelected >= 50;
          // Last category becomes navigable once the user has any meaningful
          // selection across the session (capHit OR they've grown the board).
          // Earlier categories still need at least one tap in the current cat
          // before advancing — keeps the per-category exploration habit.
          const canAdvance = isLast
            ? (categorySelected >= 1 || capHit || totalSelected >= 5)
            : categorySelected >= 1;
          return (
            <motion.button
              onClick={onNext}
              disabled={!canAdvance}
              className="w-full py-4 rounded-full text-lg font-medium flex items-center justify-center gap-2"
              style={{
                fontFamily: "'Manrope', sans-serif",
                background: canAdvance ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.03)',
                backdropFilter: 'blur(20px)',
                border: canAdvance ? '1px solid rgba(255,255,255,0.18)' : '1px solid rgba(255,255,255,0.06)',
                color: canAdvance ? 'rgba(250,249,246,0.9)' : 'rgba(255,255,255,0.3)',
                boxShadow: canAdvance ? '0 8px 32px rgba(0,0,0,0.2)' : 'none',
                cursor: canAdvance ? 'pointer' : 'not-allowed',
              }}
              whileHover={canAdvance ? { background: 'rgba(255,255,255,0.14)' } : {}}
              whileTap={canAdvance ? { scale: 0.98 } : {}}
              data-testid="next-category-btn"
            >
              {isLast ? (
                <>Continue to Words <ArrowRight size={18} /></>
              ) : (
                <>Next Category <ArrowRight size={18} /></>
              )}
            </motion.button>
          );
        })()}
      </div>

      {/* Expanded Image Modal */}
      <AnimatePresence>
        {expandedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-8"
            onClick={() => setExpandedImage(null)}
          >
            <motion.img
              src={expandedImage.url}
              alt=""
              className="max-w-full max-h-full rounded-2xl object-contain"
              initial={{ scale: 0.8 }}
              animate={{ scale: 1 }}
              exit={{ scale: 0.8 }}
            />
            <button 
              className="absolute top-6 right-6 p-3 rounded-full bg-white/10"
              onClick={() => setExpandedImage(null)}
            >
              <X size={24} className="text-white" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Word Selection Screen
const WordSelectionScreen = ({ selectedWords, onSelect, onNext, onBack }) => {
  const [customWord, setCustomWord] = useState('');
  const [activeCategory, setActiveCategory] = useState('core');
  
  const wordCategories = [
    { id: 'core', name: 'Core Identity' },
    { id: 'wealth', name: 'Wealth' },
    { id: 'relationship', name: 'Relationship' },
    { id: 'performance', name: 'Performance' },
    { id: 'healing', name: 'Healing' },
    { id: 'spiritual', name: 'Spiritual' },
    { id: 'evolution', name: 'Evolution' },
    { id: 'body', name: 'Body' },
    { id: 'freedom', name: 'Freedom' },
  ];
  
  const handleAddCustom = () => {
    if (customWord.trim() && !selectedWords.includes(customWord.trim())) {
      onSelect(customWord.trim());
      setCustomWord('');
    }
  };

  return (
    <motion.div 
      className="fixed inset-0 flex flex-col"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Cinematic bg */}
      <div className="fixed inset-0 z-0">
        <img src="https://images.pexels.com/photos/23499343/pexels-photo-23499343.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover" style={{ filter: 'brightness(0.45) saturate(0.7)' }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(10,15,20,0.55) 0%, rgba(10,15,20,0.88) 40%, rgba(10,15,20,0.96) 100%)' }} />
      </div>

      {/* Header */}
      <div className="relative z-10 flex-shrink-0 px-6 pt-6 pb-4">
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={onBack}
            className="p-2 rounded-full transition-colors"
            style={{ background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            <ArrowLeft size={20} className="text-white/50" />
          </button>
          
          <div className="px-3 py-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.08)', fontFamily: "'Manrope', sans-serif" }}>
            <span className="text-white/60 text-sm font-medium">{selectedWords.length} / 50</span>
          </div>
        </div>

        <div className="text-center mb-6">
          <h1 
            className="text-3xl text-[#FAF9F6] mb-3"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Who are you becoming?
          </h1>
          <p className="text-white/50 text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Tap a category below, then select words that resonate
          </p>
          {selectedWords.length === 0 && (
            <motion.p 
              className="text-white/30 text-xs mt-2"
              style={{ fontFamily: "'Manrope', sans-serif" }}
              animate={{ opacity: [0.5, 1, 0.5] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              Start by choosing a category
            </motion.p>
          )}
        </div>

        {/* Category tabs */}
        <div className="flex overflow-x-auto gap-2 pb-2 scrollbar-hide">
          {wordCategories.map((cat) => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`px-4 py-2 rounded-full text-xs whitespace-nowrap transition-all ${
                activeCategory === cat.id
                  ? 'text-[#FAF9F6]'
                  : 'text-white/40'
              }`}
              style={{ 
                fontFamily: "'Manrope', sans-serif",
                background: activeCategory === cat.id ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.02)',
                border: activeCategory === cat.id ? '1px solid rgba(255,255,255,0.15)' : '1px solid transparent',
              }}
            >
              {cat.name}
            </button>
          ))}
        </div>
      </div>

      {/* Word chips — relative+z-10 so they ALWAYS sit above the
          fixed-position bg image, on every browser (desktop Safari was
          painting them behind the image). */}
      <div className="relative z-10 flex-1 overflow-y-auto px-6 pb-32" data-testid="word-selection-body">
        {/* Custom word input */}
        <div className="flex gap-3 mb-6">
          <input
            type="text"
            value={customWord}
            onChange={(e) => setCustomWord(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleAddCustom()}
            placeholder="Add your own word..."
            className="flex-1 px-5 py-3 rounded-full bg-white/5 border border-white/10 text-white placeholder:text-white/30 focus:border-white/25 focus:outline-none transition-colors"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          />
          <button
            onClick={handleAddCustom}
            disabled={!customWord.trim()}
            className="px-5 py-3 rounded-full text-white/70 hover:text-white/90 transition-colors disabled:opacity-50"
            style={{ background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.12)' }}
          >
            <Plus size={20} />
          </button>
        </div>

        <div className="flex flex-wrap gap-3 justify-center" data-testid="word-grid">
          {(IDENTITY_WORDS[activeCategory] || []).map((word) => {
            const isSelected = selectedWords.includes(word);
            // Plain <button> (not motion.button) so framer-motion mount
            // animation glitches can never leave words invisible. Fade-in
            // is handled by a single parent CSS transition instead.
            return (
              <button
                key={word}
                onClick={() => onSelect(word)}
                disabled={selectedWords.length >= 50 && !isSelected}
                className="px-6 py-3 rounded-full text-sm font-medium transition-all duration-200 hover:scale-[1.03] active:scale-[0.97]"
                style={{
                  fontFamily: "'Manrope', sans-serif",
                  background: isSelected ? 'rgba(255,255,255,0.12)' : 'rgba(255,255,255,0.04)',
                  backdropFilter: 'blur(8px)',
                  border: isSelected ? '1px solid rgba(255,255,255,0.30)' : '1px solid rgba(255,255,255,0.10)',
                  color: isSelected ? 'rgba(250,249,246,0.95)' : 'rgba(255,255,255,0.7)',
                  boxShadow: isSelected ? '0 0 18px rgba(255,255,255,0.08)' : 'none',
                  opacity: 1,
                }}
                data-testid={`word-${word}`}
              >
                {word}
              </button>
            );
          })}
        </div>
      </div>

      {/* Fixed Bottom CTA */}
      <div className="fixed bottom-0 left-0 right-0 p-6 pb-8 z-20" style={{ background: 'linear-gradient(to top, rgba(10,15,20,0.95) 0%, rgba(10,15,20,0.8) 60%, transparent 100%)' }}>
        <motion.button
          onClick={onNext}
          disabled={selectedWords.length < 5}
          className="w-full py-4 rounded-full text-lg font-medium flex items-center justify-center gap-2"
          style={{ 
            fontFamily: "'Manrope', sans-serif",
            background: selectedWords.length >= 5 ? 'rgba(255,255,255,0.08)' : 'rgba(255,255,255,0.03)',
            backdropFilter: 'blur(20px)',
            border: selectedWords.length >= 5 ? '1px solid rgba(255,255,255,0.18)' : '1px solid rgba(255,255,255,0.06)',
            color: selectedWords.length >= 5 ? 'rgba(250,249,246,0.9)' : 'rgba(255,255,255,0.3)',
            boxShadow: selectedWords.length >= 5 ? '0 8px 32px rgba(0,0,0,0.2)' : 'none',
            cursor: selectedWords.length >= 5 ? 'pointer' : 'not-allowed',
          }}
          whileHover={selectedWords.length >= 5 ? { background: 'rgba(255,255,255,0.14)' } : {}}
          whileTap={selectedWords.length >= 5 ? { scale: 0.98 } : {}}
          data-testid="continue-words-btn"
        >
          {selectedWords.length >= 5 ? `Continue with ${selectedWords.length} words` : `Select ${5 - selectedWords.length} more words`} <ArrowRight size={18} className="inline ml-2" />
        </motion.button>
      </div>
    </motion.div>
  );
};

// Personal Library (Premium)
const PersonalLibraryScreen = ({ personalImages, onUpload, onNext, onBack, isPremium }) => {
  const fileInputRef = useRef(null);

  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    files.forEach(file => {
      const reader = new FileReader();
      reader.onload = (event) => {
        onUpload({ url: event.target.result, isPersonal: true });
      };
      reader.readAsDataURL(file);
    });
  };

  if (!isPremium) {
    return (
      <motion.div 
        className="fixed inset-0 flex flex-col items-center justify-center px-8"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <div className="fixed inset-0 z-0">
          <img src="https://images.pexels.com/photos/23499343/pexels-photo-23499343.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover" style={{ filter: 'brightness(0.45) saturate(0.7)' }} />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(10,15,20,0.55) 0%, rgba(10,15,20,0.9) 100%)' }} />
        </div>
        <div className="relative z-10 text-center max-w-md">
          <div className="w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-6" style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)' }}>
            <Lock size={32} className="text-white/30" />
          </div>
          <h2 
            className="text-2xl text-[#FAF9F6] mb-3"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Personal Library
          </h2>
          <p className="text-white/50 mb-8" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Upload your own meaningful images with Premium
          </p>
          <div className="flex gap-4 justify-center">
            <button
              onClick={onBack}
              className="px-6 py-3 rounded-full bg-white/5 text-white/70 hover:bg-white/10 transition-colors"
            >
              Back
            </button>
            <button
              onClick={onNext}
              className="px-8 py-3 rounded-full text-[#FAF9F6]"
              style={{ background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.18)' }}
            >
              Skip to Reveal
            </button>
          </div>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div 
      className="fixed inset-0 flex flex-col"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="fixed inset-0 z-0">
        <img src="https://images.pexels.com/photos/23499343/pexels-photo-23499343.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover" style={{ filter: 'brightness(0.45) saturate(0.7)' }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(10,15,20,0.55) 0%, rgba(10,15,20,0.88) 40%, rgba(10,15,20,0.96) 100%)' }} />
      </div>

      <div className="relative z-10 flex-shrink-0 px-6 pt-6 pb-4">
        <div className="flex items-center justify-between mb-6">
          <button onClick={onBack} className="p-2 rounded-full bg-white/5 hover:bg-white/10 transition-colors">
            <ArrowLeft size={20} className="text-white/60" />
          </button>
          <div className="flex items-center gap-2 px-3 py-1.5 rounded-full bg-amber-500/20 border border-amber-500/30">
            <Sparkles size={14} className="text-amber-400" />
            <span className="text-amber-400 text-sm font-medium">Premium</span>
          </div>
        </div>

        <div className="text-center mb-6">
          <h1 
            className="text-2xl text-[#FAF9F6] mb-2"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Your Personal Library
          </h1>
          <p className="text-white/50 text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>
            Add personal moments to make this yours
          </p>
        </div>
      </div>

      <div className="relative z-10 flex-1 overflow-y-auto px-6 pb-32 flex flex-col items-center justify-center">
        {personalImages.length === 0 ? (
          <motion.div 
            className="flex flex-col items-center justify-center text-center w-full max-w-sm"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
          >
            <motion.button
              onClick={() => fileInputRef.current?.click()}
              className="w-full aspect-[4/3] rounded-2xl flex flex-col items-center justify-center gap-4 cursor-pointer mb-6"
              style={{
                background: 'rgba(255,255,255,0.04)',
                backdropFilter: 'blur(12px)',
                border: '1px dashed rgba(255,255,255,0.18)',
                boxShadow: '0 8px 32px rgba(0,0,0,0.15)',
              }}
              whileHover={{ background: 'rgba(255,255,255,0.08)', borderColor: 'rgba(255,255,255,0.28)' }}
              whileTap={{ scale: 0.98 }}
              data-testid="upload-area-btn"
            >
              <Upload size={36} className="text-white/40" />
              <p className="text-white/55 text-base" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Tap to add your images
              </p>
              <p className="text-white/25 text-xs" style={{ fontFamily: "'Manrope', sans-serif" }}>
                Photos, screenshots, moments
              </p>
            </motion.button>

            <p className="text-white/25 text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Or skip this step — you can always add later
            </p>
          </motion.div>
        ) : (
          <div className="w-full">
            <div className="columns-2 gap-3 space-y-3">
              {personalImages.map((image, index) => (
                <div key={index} className="break-inside-avoid rounded-2xl overflow-hidden">
                  <img src={image.url} alt="" className="w-full h-auto" />
                </div>
              ))}
            </div>
            <motion.button
              onClick={() => fileInputRef.current?.click()}
              className="w-full mt-6 py-4 rounded-full flex items-center justify-center gap-2 text-white/50"
              style={{ background: 'rgba(255,255,255,0.04)', border: '1px dashed rgba(255,255,255,0.15)' }}
              whileHover={{ background: 'rgba(255,255,255,0.08)' }}
              whileTap={{ scale: 0.99 }}
            >
              <Plus size={20} /> Add More
            </motion.button>
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          multiple
          onChange={handleFileChange}
          className="hidden"
        />
      </div>

      <div className="fixed bottom-0 left-0 right-0 p-6 pb-8 z-20" style={{ background: 'linear-gradient(to top, rgba(10,15,20,0.95) 0%, rgba(10,15,20,0.8) 60%, transparent 100%)' }}>
        <motion.button
          onClick={onNext}
          className="w-full py-4 rounded-full text-lg font-medium"
          style={{ fontFamily: "'Manrope', sans-serif", background: 'rgba(255,255,255,0.08)', backdropFilter: 'blur(20px)', border: '1px solid rgba(255,255,255,0.18)', color: 'rgba(250,249,246,0.9)', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }}
          whileHover={{ background: 'rgba(255,255,255,0.14)' }}
          whileTap={{ scale: 0.98 }}
        >
          Continue to Reveal <ArrowRight size={18} className="inline ml-2" />
        </motion.button>
      </div>
    </motion.div>
  );
};



// Affirmation Selection Screen - Before Immersion
const AffirmationSelectionScreen = ({ 
  selectedAffirmations, 
  onToggle, 
  onNext, 
  onBack,
  availableAffirmations,
  showImmersionChoice = false
}) => {
  const [, setMode] = useAudioMode();
  return (
    <motion.div 
      className="fixed inset-0 flex flex-col"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      {/* Cinematic bg */}
      <div className="fixed inset-0 z-0">
        <img src="https://images.pexels.com/photos/23499343/pexels-photo-23499343.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" alt="" className="w-full h-full object-cover" style={{ filter: 'brightness(0.4) saturate(0.7)' }} />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(10,15,20,0.55) 0%, rgba(10,15,20,0.88) 40%, rgba(10,15,20,0.96) 100%)' }} />
      </div>

      {/* Header */}
      <div className="relative z-10 flex-shrink-0 px-6 pt-6 pb-4">
        <div className="flex items-center justify-between mb-6">
          <button 
            onClick={onBack}
            className="p-2 rounded-full transition-colors"
            style={{ background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            <ArrowLeft size={20} className="text-white/60" />
          </button>
          
          <div className="px-3 py-1.5 rounded-full" style={{ background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(12px)', border: '1px solid rgba(255,255,255,0.08)', fontFamily: "'Manrope', sans-serif" }}>
            <span className="text-white/60 text-sm font-medium">{selectedAffirmations.length} selected</span>
          </div>
        </div>

        <div className="text-center mb-4">
          <h1 
            className="text-2xl md:text-3xl text-[#FAF9F6] mb-3"
            style={{ fontFamily: "'Playfair Display', serif" }}
          >
            Choose Your Affirmations
          </h1>
          <p className="text-white/50 text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>
            These will be spoken softly during your immersion
          </p>
        </div>
      </div>

      {/* Affirmation List */}
      <div className="relative z-10 flex-1 overflow-y-auto px-6 pb-32">
        <div className="space-y-3">
          {availableAffirmations.map((affirmation, index) => {
            const isSelected = selectedAffirmations.includes(affirmation);
            return (
              <motion.button
                key={affirmation}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: index * 0.03 }}
                onClick={() => onToggle(affirmation)}
                className="w-full text-left p-4 rounded-2xl transition-all duration-300"
                style={{
                  background: isSelected ? 'rgba(255,255,255,0.06)' : 'rgba(255,255,255,0.02)',
                  backdropFilter: 'blur(8px)',
                  border: isSelected ? '1px solid rgba(255,255,255,0.18)' : '1px solid rgba(255,255,255,0.06)',
                }}
              >
                <div className="flex items-start gap-3">
                  <div className={`w-5 h-5 rounded-full flex-shrink-0 mt-0.5 flex items-center justify-center transition-all ${
                    isSelected 
                      ? 'bg-white/20' 
                      : 'border border-white/30'
                  }`}>
                    {isSelected && <Check size={12} className="text-white" />}
                  </div>
                  <p 
                    className={`text-sm leading-relaxed transition-colors ${
                      isSelected ? 'text-[#FAF9F6]' : 'text-white/70'
                    }`}
                    style={{ fontFamily: "'Manrope', sans-serif" }}
                  >
                    {affirmation}
                  </p>
                </div>
              </motion.button>
            );
          })}
        </div>
      </div>

      {/* Fixed Bottom CTA — universal AudioEntry */}
      <div className="fixed bottom-0 left-0 right-0 px-6 pb-8 pt-6 z-20" style={{ background: 'linear-gradient(to top, rgba(10,15,20,0.95) 0%, rgba(10,15,20,0.8) 60%, transparent 100%)' }}>
        {selectedAffirmations.length < 5 ? (
          <p className="text-center text-white/40 text-sm py-6" style={{ fontFamily: "'Spectral', serif", fontStyle: 'italic' }}>
            Select at least 5 affirmations to begin
          </p>
        ) : (
          <AudioEntry
            defaultMode="soft"
            area="abundance_vision_board"
            onSelect={(picked) => { setMode(picked); onNext('still'); }}
          />
        )}

        {/* Video Immersion option */}
        {showImmersionChoice && selectedAffirmations.length >= 5 && (
          <motion.button
            onClick={() => onNext('video')}
            className="w-full py-3 rounded-full text-sm font-medium flex items-center justify-center gap-2 mt-3"
            style={{
              fontFamily: "'Manrope', sans-serif",
              background: 'rgba(255,255,255,0.04)',
              border: '1px solid rgba(255,255,255,0.08)',
              color: 'rgba(250,249,246,0.5)',
              cursor: 'pointer',
            }}
            whileHover={{ background: 'rgba(255,255,255,0.08)', color: 'rgba(250,249,246,0.8)' }}
            whileTap={{ scale: 0.98 }}
            data-testid="begin-video-immersion-btn"
          >
            <Play size={14} /> Video Immersion
          </motion.button>
        )}
      </div>
    </motion.div>
  );
};

// Immersion music tracks - Rotate between 3 tracks (day & night)
const IMMERSION_TRACKS = [
  'immersion/immersion_1.mp3',
  'immersion/immersion_2.mp3',
  'soft_return_loop_2.mp3',
];

// Immersion Mode (3-minute cinematic experience with subliminal layer + music + affirmations)
const ImmersionScreen = ({ selectedImages, selectedWords, selectedAffirmations = [], onComplete, onViewBoard }) => {
  const [progress, setProgress] = useState(0);
  const [currentSceneIndex, setCurrentSceneIndex] = useState(0);
  const [nextSceneIndex, setNextSceneIndex] = useState(1);
  const [transitioning, setTransitioning] = useState(false);
  const [flashItem, setFlashItem] = useState(null);
  const [muted, setMuted] = useState(false);
  const [paused, setPaused] = useState(false);
  const [currentAffirmationIndex, setCurrentAffirmationIndex] = useState(0);
  const [showAffirmation, setShowAffirmation] = useState(false);
  const [audioReady, setAudioReady] = useState(false);
  const [showViewBoardHint, setShowViewBoardHint] = useState(false);
  const [reverseTransition, setReverseTransition] = useState(false);
  const [reversePhase, setReversePhase] = useState(0);
  const [audioUnlocked, setAudioUnlocked] = useState(false);
  const [breathPhase, setBreathPhase] = useState('in'); // in | hold | out
  const [showBreathGuide, setShowBreathGuide] = useState(true);
  
  const duration = 110; // ~2 minutes — V3 emotional arc
  const progressRef = useRef(0);
  const sceneTimerRef = useRef(null);
  const flashTimerRef = useRef(null);
  const musicRef = useRef(null);
  const affirmationTimerRef = useRef(null);
  const voiceAudioRef = useRef(null);
  const affirmationIndexRef = useRef(0);
  const breathCycleRef = useRef(0); // 0-1 breath phase for syncing
  const lastFlashImageRef = useRef(null); // avoid consecutive image dupes
  const lastFlashWordRef = useRef(null);  // avoid consecutive word dupes
  
  // Show "View my board" hint after 15 seconds
  useEffect(() => {
    const hintTimer = setTimeout(() => {
      setShowViewBoardHint(true);
    }, 15000);
    return () => clearTimeout(hintTimer);
  }, []);

  // Breathing Guide — Breathe in 6s, Hold 4s, Release 8s (18s cycle)
  // Only active during Safety phase (first ~30s), then fades
  useEffect(() => {
    if (!audioUnlocked || paused) return;
    
    const BREATHE_IN = 6000;
    const HOLD = 4000;
    const RELEASE = 8000;
    const CYCLE = BREATHE_IN + HOLD + RELEASE; // 18s
    
    let elapsed = 0;
    let animFrame;
    const start = Date.now();
    
    const tick = () => {
      elapsed = Date.now() - start;
      const inCycle = elapsed % CYCLE;
      
      if (inCycle < BREATHE_IN) {
        setBreathPhase('in');
      } else if (inCycle < BREATHE_IN + HOLD) {
        setBreathPhase('hold');
      } else {
        setBreathPhase('out');
      }
      
      // Fade out after 30s (end of Safety phase)
      if (elapsed > 28000) {
        setShowBreathGuide(false);
        return;
      }
      
      animFrame = requestAnimationFrame(tick);
    };
    
    // Start after a brief 2s settle
    const startTimer = setTimeout(() => {
      animFrame = requestAnimationFrame(tick);
    }, 2000);
    
    return () => {
      clearTimeout(startTimer);
      if (animFrame) cancelAnimationFrame(animFrame);
    };
  }, [audioUnlocked, paused]);

  // Resolve track from the unified audio system (single source of truth)
  // Falls back to IMMERSION_TRACKS rotation if the API is unavailable.
  const [resolvedTrackUrl, setResolvedTrackUrl] = useState(null);
  useEffect(() => {
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    const mode = (typeof window !== 'undefined' && window.localStorage.getItem('reclaim_audio_mode')) || 'soft';
    let cancelled = false;
    fetch(`${API_URL}/api/unified-audio/resolve?area=abundance_vision_board&mode=${mode}`)
      .then((r) => r.json())
      .then((d) => {
        if (cancelled) return;
        if (d?.music?.url) {
          setResolvedTrackUrl(d.music.url.startsWith('http') ? d.music.url : `${API_URL}${d.music.url}`);
        }
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  const selectedTrack = useMemo(() => {
    const trackIndex = Math.floor(Math.random() * IMMERSION_TRACKS.length);
    return IMMERSION_TRACKS[trackIndex];
  }, []);

  // Initialize background music (but DON'T play until user gesture)
  useEffect(() => {
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    const musicUrl = resolvedTrackUrl || `${API_URL}/api/audio/ambient/${selectedTrack}`;
    musicRef.current = new Audio(musicUrl);
    musicRef.current.loop = true;
    musicRef.current.volume = 0;
    
    // Pre-load the audio file
    musicRef.current.load();
    
    return () => {
      if (musicRef.current) {
        musicRef.current.pause();
        musicRef.current = null;
      }
      if (voiceAudioRef.current) {
        voiceAudioRef.current.pause();
        voiceAudioRef.current = null;
      }
    };
  }, [selectedTrack, resolvedTrackUrl]);

  // Start audio - MUST be called from a user gesture (click) for mobile Safari
  const startAudio = useCallback(() => {
    setAudioUnlocked(true);
    // Auto-tick Daily Essentials · Daily Immersion (session-scoped dedupe).
    trackImmersionPlayed();
    if (musicRef.current && !muted && !paused) {
      musicRef.current.play().then(() => {
        setAudioReady(true);
        let vol = 0;
        const interval = setInterval(() => {
          vol += 0.01;
          if (vol >= 0.3 || !musicRef.current) {
            clearInterval(interval);
            if (musicRef.current) musicRef.current.volume = 0.3;
          } else {
            musicRef.current.volume = vol;
          }
        }, 80);
      }).catch(err => {
        console.log('Audio play failed:', err);
        setAudioReady(true);
      });
    } else {
      setAudioReady(true);
    }
  }, [muted, paused]);

  // Auto-start audio when component mounts (works on desktop, silently fails on mobile)
  useEffect(() => {
    startAudio();
  }, [startAudio]);

  // Handle mute/pause for music
  useEffect(() => {
    if (musicRef.current) {
      if (muted || paused) {
        musicRef.current.pause();
      } else if (audioReady) {
        musicRef.current.play().catch(err => console.log('Audio play failed:', err));
      }
    }
    if (voiceAudioRef.current) {
      if (muted || paused) {
        voiceAudioRef.current.pause();
      }
    }
  }, [muted, paused, audioReady]);

  // Affirmation display timer - cycles through ALL selected affirmations
  // Uses ref for index to avoid stale closure issues
  // Plays TTS audio at subliminal volume (like 21 Night Integration)
  useEffect(() => {
    if (paused || selectedAffirmations.length === 0) return;
    
    const API_URL = process.env.REACT_APP_BACKEND_URL || '';
    affirmationIndexRef.current = 0;
    
    const playAffirmationTTS = async (text) => {
      try {
        const response = await fetch(`${API_URL}/api/immersion/affirmation`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ text }),
        });
        
        if (response.ok) {
          const data = await response.json();
          if (data.audio_url && !muted) {
            // Stop previous voice if still playing
            if (voiceAudioRef.current) {
              voiceAudioRef.current.pause();
              voiceAudioRef.current = null;
            }
            voiceAudioRef.current = new Audio(data.audio_url);
            voiceAudioRef.current.volume = 0.2; // Subliminal — soft whisper level
            voiceAudioRef.current.play().catch(err => console.log('TTS play failed:', err));
          }
        }
      } catch (error) {
        console.log('TTS generation failed, displaying text only:', error);
      }
    };
    
    const showNextAffirmation = () => {
      const idx = affirmationIndexRef.current;
      const currentAffirmation = selectedAffirmations[idx];
      if (!currentAffirmation) return;
      
      setCurrentAffirmationIndex(idx);
      setShowAffirmation(true);
      
      if (!muted) {
        playAffirmationTTS(currentAffirmation);
      }
      
      // Hide after 5 seconds
      setTimeout(() => setShowAffirmation(false), 5000);
      // Advance to next affirmation (wraps around)
      affirmationIndexRef.current = (idx + 1) % selectedAffirmations.length;
    };
    
    // First affirmation after 12 seconds (nervous system fully settles in Safety phase)
    const initialTimer = setTimeout(showNextAffirmation, 12000);
    
    // Then every 12 seconds — slow, spacious
    affirmationTimerRef.current = setInterval(showNextAffirmation, 12000);
    
    return () => {
      clearTimeout(initialTimer);
      if (affirmationTimerRef.current) clearInterval(affirmationTimerRef.current);
      if (voiceAudioRef.current) {
        voiceAudioRef.current.pause();
        voiceAudioRef.current = null;
      }
    };
  }, [paused, selectedAffirmations, muted]);
  
  // V3 Emotional Arc phases
  const getCurrentPhase = useCallback((prog) => {
    const seconds = (prog / 100) * duration;
    if (seconds < 30) return 'safety';        // 0-30s: Warmth, grounding
    if (seconds < 70) return 'opening';       // 30-70s: Expansion, breathing deepens
    if (seconds < 105) return 'becoming';     // 70-105s: Identity installation, subliminal peak
    if (seconds < 110) return 'installation'; // 105-110s: Deep pause, stillness
    return 'integration';                     // 110+: Gentle return
  }, [duration]);

  // Subliminal flash engine — organic rhythm, 1-5% opacity
  const triggerFlash = useCallback(() => {
    const currentPhase = getCurrentPhase(progressRef.current);
    const isDeep = currentPhase === 'becoming' || currentPhase === 'installation';
    
    // Opacity: barely perceivable
    const opacityConfig = isDeep ? SUBLIMINAL_CONFIG.deepOpacity : SUBLIMINAL_CONFIG.opacity;
    const opacity = opacityConfig.min + Math.random() * (opacityConfig.max - opacityConfig.min);
    
    // Duration: longer, softer presence
    const flashDuration = SUBLIMINAL_CONFIG.flashDuration.min + 
      Math.random() * (SUBLIMINAL_CONFIG.flashDuration.max - SUBLIMINAL_CONFIG.flashDuration.min);
    
    // Positioning: 40% center, 60% peripheral
    let position;
    if (Math.random() < SUBLIMINAL_CONFIG.positioning.center) {
      // Center placement
      position = {
        left: `${35 + Math.random() * 30}%`,
        top: `${35 + Math.random() * 30}%`,
      };
    } else {
      // Peripheral placement
      const edges = ['left', 'right', 'top-left', 'top-right', 'bottom-left', 'bottom-right'];
      const edge = edges[Math.floor(Math.random() * edges.length)];
      switch (edge) {
        case 'left':
          position = { left: `${5 + Math.random() * 15}%`, top: `${20 + Math.random() * 60}%` };
          break;
        case 'right':
          position = { left: `${80 + Math.random() * 15}%`, top: `${20 + Math.random() * 60}%` };
          break;
        case 'top-left':
          position = { left: `${5 + Math.random() * 25}%`, top: `${5 + Math.random() * 20}%` };
          break;
        case 'top-right':
          position = { left: `${70 + Math.random() * 25}%`, top: `${5 + Math.random() * 20}%` };
          break;
        case 'bottom-left':
          position = { left: `${5 + Math.random() * 25}%`, top: `${75 + Math.random() * 20}%` };
          break;
        case 'bottom-right':
        default:
          position = { left: `${70 + Math.random() * 25}%`, top: `${75 + Math.random() * 20}%` };
      }
    }
    
    // Choose content: image or word (avoid repeating the previous one)
    const isImage = Math.random() > 0.4 && selectedImages.length > 0;
    let content;
    if (isImage) {
      // Pick an image that's not the same as the last one shown
      let pick = selectedImages[Math.floor(Math.random() * selectedImages.length)];
      if (selectedImages.length > 1 && pick?.url === lastFlashImageRef.current) {
        for (let attempt = 0; attempt < 4; attempt++) {
          const candidate = selectedImages[Math.floor(Math.random() * selectedImages.length)];
          if (candidate?.url !== lastFlashImageRef.current) { pick = candidate; break; }
        }
      }
      lastFlashImageRef.current = pick?.url || null;
      content = pick;
    } else if (selectedWords.length > 0) {
      let pick = selectedWords[Math.floor(Math.random() * selectedWords.length)];
      if (selectedWords.length > 1 && pick === lastFlashWordRef.current) {
        for (let attempt = 0; attempt < 4; attempt++) {
          const candidate = selectedWords[Math.floor(Math.random() * selectedWords.length)];
          if (candidate !== lastFlashWordRef.current) { pick = candidate; break; }
        }
      }
      lastFlashWordRef.current = pick;
      content = pick;
    } else {
      content = null;
    }
    
    setFlashItem({
      type: isImage ? 'image' : 'word',
      content,
      opacity,
      position,
      duration: flashDuration,
    });
    
    // Clear flash after duration
    setTimeout(() => setFlashItem(null), flashDuration + SUBLIMINAL_CONFIG.fadeInOut * 2);
    
    // Schedule next flash — organic, no fixed pattern
    const intervalConfig = isDeep 
      ? SUBLIMINAL_CONFIG.deepImmersionInterval 
      : SUBLIMINAL_CONFIG.baseFlashInterval;
    const nextInterval = intervalConfig.min + Math.random() * (intervalConfig.max - intervalConfig.min);
    
    flashTimerRef.current = setTimeout(triggerFlash, nextInterval);
  }, [selectedImages, selectedWords, getCurrentPhase]);

  // Scene transition handler
  const transitionToNextScene = useCallback(() => {
    if (currentSceneIndex >= IMMERSION_SCENES.length - 1) return;
    
    setTransitioning(true);
    setTimeout(() => {
      setCurrentSceneIndex(prev => prev + 1);
      setNextSceneIndex(prev => Math.min(prev + 1, IMMERSION_SCENES.length - 1));
      setTransitioning(false);
    }, 1500); // 1.5s crossfade
  }, [currentSceneIndex]);

  // Main progress timer with soft ending
  useEffect(() => {
    if (paused) return;
    
    const progressInterval = setInterval(() => {
      setProgress(prev => {
        const newProgress = prev + (100 / duration);
        progressRef.current = newProgress;
        
        if (newProgress >= 100) {
          clearInterval(progressInterval);
          // Soft ending - let the final integration phase settle
          setTimeout(onComplete, 2500);
          return 100;
        }
        return newProgress;
      });
    }, 1000);
    
    return () => clearInterval(progressInterval);
  }, [paused, duration, onComplete]);
  
  // Ending state for soft transition
  const isEnding = progress >= 95;

  // ============================================
  // REVERSE TRANSITION: Immersion → Reveal
  // "Your vision is re-forming"
  // ============================================
  const handleViewBoard = useCallback(() => {
    if (reverseTransition) return;
    
    setReverseTransition(true);
    setReversePhase(1);
    
    // Fade out music gently
    if (musicRef.current) {
      let vol = musicRef.current.volume;
      const fadeInterval = setInterval(() => {
        vol -= 0.05;
        if (vol <= 0 || !musicRef.current) {
          clearInterval(fadeInterval);
          if (musicRef.current) {
            musicRef.current.pause();
            musicRef.current.volume = 0;
          }
        } else {
          musicRef.current.volume = vol;
        }
      }, 100);
    }
    
    // Phase timing for reverse dissolve
    setTimeout(() => setReversePhase(2), 400);   // Nature begins to soften
    setTimeout(() => setReversePhase(3), 1000);  // Board ghost appears
    setTimeout(() => setReversePhase(4), 1800);  // Images drift back
    setTimeout(() => {
      if (onViewBoard) onViewBoard();
    }, 2800);
  }, [reverseTransition, onViewBoard]);

  // Preload next scene images for smooth transitions
  useEffect(() => {
    const preload = (idx) => {
      if (IMMERSION_SCENES[idx]) {
        const img = new Image();
        img.src = IMMERSION_SCENES[idx].url;
      }
    };
    preload(currentSceneIndex + 1);
    preload(currentSceneIndex + 2);
  }, [currentSceneIndex]);

  // Scene transition timer
  useEffect(() => {
    if (paused) return;
    
    const currentScene = IMMERSION_SCENES[currentSceneIndex];
    if (!currentScene) return;
    
    sceneTimerRef.current = setTimeout(transitionToNextScene, currentScene.duration);
    
    return () => {
      if (sceneTimerRef.current) clearTimeout(sceneTimerRef.current);
    };
  }, [currentSceneIndex, paused, transitionToNextScene]);

  // Start subliminal flashes
  useEffect(() => {
    if (paused) return;
    
    // Initial delay before first flash
    flashTimerRef.current = setTimeout(triggerFlash, 2000);
    
    return () => {
      if (flashTimerRef.current) clearTimeout(flashTimerRef.current);
    };
  }, [paused, triggerFlash]);

  const currentPhase = getCurrentPhase(progress);
  const phaseTexts = {
    safety: "",
    opening: "Allow yourself to open...",
    becoming: "",
    installation: "",
    integration: "This is now within you",
  };

  const remainingSeconds = Math.max(0, Math.ceil((100 - progress) / 100 * duration));
  const remainingMinutes = Math.ceil(remainingSeconds / 60);

  return (
    <motion.div 
      className="fixed inset-0 bg-black overflow-hidden select-none"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1.5 }}
    >
      {/* Mobile audio unlock overlay */}
      {!audioUnlocked && (
        <motion.div
          className="absolute inset-0 z-[100] flex flex-col items-center justify-center cursor-pointer"
          style={{ background: 'rgba(0,0,0,0.85)' }}
          onClick={startAudio}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          <motion.div
            animate={{ scale: [1, 1.05, 1], opacity: [0.6, 1, 0.6] }}
            transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
            className="text-center"
          >
            <p className="text-white/80 text-2xl mb-3" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
              Tap to begin
            </p>
            <p className="text-white/30 text-sm" style={{ fontFamily: "'Manrope', sans-serif" }}>
              with sound
            </p>
          </motion.div>
        </motion.div>
      )}

      {/* ============================================
          BREATHING GUIDE — Safety Phase (0-30s)
          Breathe in 6s → Hold 4s → Release 8s
          ============================================ */}
      <AnimatePresence>
        {showBreathGuide && audioUnlocked && (
          <motion.div
            className="absolute inset-0 z-[90] flex flex-col items-center justify-center pointer-events-none"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 2 }}
            data-testid="breath-guide-overlay"
          >
            {/* Breathing circle — expands on inhale, holds, contracts on release */}
            <motion.div
              className="relative flex items-center justify-center"
              animate={{
                scale: breathPhase === 'in' ? 1.35 : breathPhase === 'hold' ? 1.35 : 0.85,
                opacity: breathPhase === 'hold' ? 0.7 : 0.5,
              }}
              transition={{
                scale: { 
                  duration: breathPhase === 'in' ? 6 : breathPhase === 'hold' ? 0.3 : 8, 
                  ease: breathPhase === 'in' ? [0.25, 0.1, 0.25, 1] : breathPhase === 'hold' ? 'linear' : [0.25, 0.1, 0.25, 1],
                },
                opacity: { duration: 1 },
              }}
            >
              {/* Outer glow ring */}
              <div 
                className="w-36 h-36 md:w-44 md:h-44 rounded-full"
                style={{
                  background: 'radial-gradient(circle, rgba(255,250,235,0.06) 0%, rgba(255,250,235,0.02) 50%, transparent 70%)',
                  boxShadow: '0 0 80px 20px rgba(255,250,235,0.03)',
                }}
              />
              {/* Inner ring */}
              <motion.div 
                className="absolute w-28 h-28 md:w-36 md:h-36 rounded-full"
                style={{
                  border: '1px solid rgba(255,250,235,0.08)',
                }}
                animate={{
                  borderColor: breathPhase === 'hold' 
                    ? 'rgba(255,250,235,0.14)' 
                    : 'rgba(255,250,235,0.06)',
                }}
                transition={{ duration: 1 }}
              />
            </motion.div>

            {/* Breath text — gentle, italic */}
            <motion.p
              className="absolute text-white/30 text-sm tracking-widest"
              style={{ 
                fontFamily: "'Manrope', sans-serif",
                bottom: '38%',
                letterSpacing: '0.15em',
              }}
              key={breathPhase}
              initial={{ opacity: 0, y: 5 }}
              animate={{ opacity: 0.4, y: 0 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.8 }}
            >
              {breathPhase === 'in' && 'breathe in'}
              {breathPhase === 'hold' && 'hold'}
              {breathPhase === 'out' && 'release'}
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Background scenes with crossfade + breath-linked motion */}
      <div className="absolute inset-0">
        {/* Current scene — gentle breathing scale */}
        <motion.div
          className="absolute inset-0"
          animate={{ 
            opacity: transitioning ? 0 : isEnding ? 0.6 : 1,
            scale: isEnding ? 1.08 : [1, 1.015, 1],
            filter: isEnding ? 'blur(4px) brightness(1.1)' : 'blur(0px) brightness(1)',
          }}
          transition={{ 
            opacity: { duration: isEnding ? 3 : 1.5 },
            scale: isEnding ? { duration: 3 } : { duration: 7, repeat: Infinity, ease: 'easeInOut' },
            filter: { duration: isEnding ? 3 : 1.5 },
          }}
        >
          <img 
            src={IMMERSION_SCENES[currentSceneIndex]?.url}
            alt=""
            className="w-full h-full object-cover"
            style={{ filter: 'brightness(0.55) saturate(0.85)', willChange: 'transform, opacity' }}
          />
        </motion.div>
        
        {/* Next scene (for crossfade) */}
        <motion.div
          className="absolute inset-0"
          animate={{ opacity: transitioning ? 1 : 0 }}
          transition={{ duration: 1.5 }}
        >
          <img 
            src={IMMERSION_SCENES[nextSceneIndex]?.url}
            alt=""
            className="w-full h-full object-cover"
            style={{ filter: 'brightness(0.55) saturate(0.85)', willChange: 'transform, opacity' }}
          />
        </motion.div>
        
        {/* Breath-synced radial glow — pulses with 7s inhale/exhale */}
        <motion.div 
          className="absolute inset-0 pointer-events-none"
          animate={{ opacity: [0.05, 0.15, 0.05] }}
          transition={{ duration: 7, repeat: Infinity, ease: 'easeInOut' }}
          style={{
            background: 'radial-gradient(ellipse 80% 80% at 50% 50%, rgba(255, 250, 235, 0.08) 0%, transparent 60%)',
          }}
        />
        
        {/* Dark vignette */}
        <div 
          className="absolute inset-0"
          style={{ 
            background: 'radial-gradient(circle at 50% 50%, transparent 0%, rgba(0,0,0,0.45) 100%)',
          }}
        />
        
        {/* Soft ending warm glow overlay - fades in during last 5% */}
        <motion.div 
          className="absolute inset-0 pointer-events-none"
          initial={{ opacity: 0 }}
          animate={{ 
            opacity: isEnding ? 1 : 0,
          }}
          transition={{ duration: 3, ease: 'easeOut' }}
          style={{
            background: `
              radial-gradient(ellipse 120% 100% at 50% 50%, rgba(255, 252, 245, 0.15) 0%, transparent 60%),
              linear-gradient(to top, rgba(20, 25, 22, 0.5) 0%, transparent 40%)
            `,
          }}
        />
        
        {/* Final integration message */}
        {isEnding && (
          <motion.div 
            className="absolute inset-0 flex items-center justify-center z-30"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 2, delay: 0.5 }}
          >
            <p 
              className="text-2xl md:text-3xl text-[#FAF9F6]/60 text-center px-8 max-w-xl"
              style={{ 
                fontFamily: "'Playfair Display', serif",
                textShadow: '0 0 40px rgba(0,0,0,0.5)',
              }}
            >
              This is now within you
            </p>
          </motion.div>
        )}
      </div>

      {/* Subliminal flash layer — ultra-subtle, organic fade */}
      <AnimatePresence>
        {flashItem && (
          <motion.div
            className="absolute pointer-events-none"
            style={{
              left: flashItem.position.left,
              top: flashItem.position.top,
              transform: 'translate(-50%, -50%)',
              zIndex: 30,
            }}
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: flashItem.opacity, scale: 1 }}
            exit={{ opacity: 0, scale: 1.01 }}
            transition={{ 
              duration: SUBLIMINAL_CONFIG.fadeInOut / 1000,
              ease: [0.25, 0.1, 0.25, 1],
            }}
          >
            {flashItem.type === 'image' ? (
              <img 
                src={flashItem.content.url} 
                alt="" 
                className="w-24 md:w-36 h-auto object-cover rounded-2xl"
                style={{ 
                  filter: 'blur(2px) saturate(0.7)',
                  mixBlendMode: 'screen',
                }}
              />
            ) : (
              <span 
                className="text-2xl md:text-4xl text-white/80 font-light"
                style={{ 
                  fontFamily: "'Playfair Display', serif",
                  fontStyle: 'italic',
                  textShadow: '0 0 40px rgba(255,255,255,0.15)',
                  mixBlendMode: 'soft-light',
                }}
              >
                {flashItem.content}
              </span>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Center text — appears only during specific phases */}
      <div className="absolute inset-0 flex items-center justify-center z-20 pointer-events-none">
        <AnimatePresence mode="wait">
          {phaseTexts[currentPhase] && (
            <motion.p
              key={currentPhase}
              className="text-xl md:text-2xl text-[#FAF9F6]/35 text-center px-8 max-w-lg"
              style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -15 }}
              transition={{ duration: 2 }}
            >
              {phaseTexts[currentPhase]}
            </motion.p>
          )}
        </AnimatePresence>
      </div>

      {/* Spoken Affirmation Display Layer - Very faint */}
      <AnimatePresence>
        {showAffirmation && selectedAffirmations[currentAffirmationIndex] && (
          <motion.div
            className="absolute bottom-32 left-0 right-0 flex justify-center z-25 pointer-events-none"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 0.25, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 2, ease: "easeOut" }}
          >
            <p 
              className="text-base md:text-lg text-[#FAF9F6] text-center px-12 max-w-2xl"
              style={{ 
                fontFamily: "'Playfair Display', serif",
                fontStyle: 'italic',
                textShadow: '0 0 30px rgba(0,0,0,0.6)',
                letterSpacing: '0.02em'
              }}
            >
              {selectedAffirmations[currentAffirmationIndex]}
            </p>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Progress — ultra-subtle thin line */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 w-48 z-40">
        <div className="h-px bg-white/8 rounded-full overflow-hidden">
          <motion.div
            className="h-full rounded-full"
            style={{ background: 'rgba(255,255,255,0.18)', width: `${progress}%` }}
          />
        </div>
        <p 
          className="text-center text-white/15 text-[10px] mt-3 tracking-wider"
          style={{ fontFamily: "'Manrope', sans-serif" }}
        >
          {remainingMinutes > 0 ? `${remainingMinutes} min` : ''}
        </p>
      </div>

      {/* Controls */}
      <div className="absolute top-6 right-6 flex items-center gap-3 z-40">
        <button
          onClick={() => setMuted(!muted)}
          className="p-2 rounded-full bg-black/20 text-white/30 hover:text-white/50 transition-colors"
        >
          {muted ? <VolumeX size={18} /> : <Volume2 size={18} />}
        </button>
        <button
          onClick={() => setPaused(!paused)}
          className="p-2 rounded-full bg-black/20 text-white/30 hover:text-white/50 transition-colors"
        >
          {paused ? <Play size={18} /> : <Pause size={18} />}
        </button>
        <button
          onClick={onComplete}
          className="text-white/15 hover:text-white/30 text-xs transition-colors"
          style={{ fontFamily: "'Manrope', sans-serif" }}
        >
          Skip
        </button>
      </div>

      {/* ============================================
          VIEW MY BOARD - Subtle option to return
          Appears after 15 seconds, minimal, non-intrusive
          ============================================ */}
      <AnimatePresence>
        {showViewBoardHint && !isEnding && !reverseTransition && (
          <motion.button
            onClick={handleViewBoard}
            className="absolute bottom-8 left-1/2 -translate-x-1/2 z-40 flex items-center gap-2 px-4 py-2 rounded-full transition-all"
            style={{
              fontFamily: "'Manrope', sans-serif",
              background: 'rgba(255, 255, 255, 0.06)',
              border: '1px solid rgba(255, 255, 255, 0.08)',
            }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 10 }}
            transition={{ duration: 0.6 }}
            whileHover={{ background: 'rgba(255, 255, 255, 0.12)' }}
            data-testid="view-board-btn"
          >
            <Eye size={14} className="text-white/30" />
            <span className="text-white/30 text-xs">View your vision</span>
          </motion.button>
        )}
      </AnimatePresence>

      {/* ============================================
          REVERSE TRANSITION OVERLAY
          "Your vision is re-forming"
          ============================================ */}
      <AnimatePresence>
        {reverseTransition && (
          <>
            {/* Ghost of the board appearing underneath */}
            <motion.div 
              className="absolute inset-0 z-10 overflow-hidden"
              initial={{ opacity: 0 }}
              animate={{ 
                opacity: reversePhase >= 3 ? 0.6 : reversePhase >= 2 ? 0.2 : 0,
              }}
              transition={{ duration: 1.2 }}
            >
              {/* Mini preview of the collage */}
              {selectedImages.slice(0, 8).map((image, index) => {
                const positions = [
                  { left: '35%', top: '30%', width: '35%' },
                  { left: '10%', top: '15%', width: '22%' },
                  { left: '65%', top: '10%', width: '20%' },
                  { left: '5%', top: '50%', width: '18%' },
                  { left: '70%', top: '55%', width: '22%' },
                  { left: '25%', top: '65%', width: '16%' },
                  { left: '55%', top: '70%', width: '15%' },
                  { left: '80%', top: '35%', width: '14%' },
                ];
                const pos = positions[index] || positions[0];
                
                return (
                  <motion.div
                    key={image.url}
                    className="absolute rounded-xl overflow-hidden"
                    style={{
                      left: pos.left,
                      top: pos.top,
                      width: pos.width,
                      aspectRatio: index === 0 ? '4/3' : '1',
                      boxShadow: '0 15px 40px rgba(0,0,0,0.5)',
                    }}
                    initial={{ 
                      opacity: 0, 
                      scale: 0.8,
                      y: 40,
                      filter: 'blur(15px)',
                    }}
                    animate={{ 
                      opacity: reversePhase >= 4 ? 1 : reversePhase >= 3 ? 0.5 : 0,
                      scale: reversePhase >= 4 ? 1 : 0.9,
                      y: reversePhase >= 4 ? 0 : 20,
                      filter: reversePhase >= 4 ? 'blur(0px)' : 'blur(8px)',
                    }}
                    transition={{ 
                      duration: 1.2, 
                      delay: index === 0 ? 0.4 : 0.1 + index * 0.08,
                      ease: 'easeOut',
                    }}
                  >
                    <img 
                      src={image.url} 
                      alt="" 
                      className="w-full h-full object-cover"
                    />
                  </motion.div>
                );
              })}
            </motion.div>

            {/* Warm glow as board emerges */}
            <motion.div 
              className="absolute inset-0 pointer-events-none z-15"
              initial={{ opacity: 0 }}
              animate={{ 
                opacity: reversePhase >= 2 ? 0.25 : 0,
              }}
              transition={{ duration: 1.5 }}
              style={{
                background: 'radial-gradient(ellipse 100% 100% at 50% 50%, rgba(255, 250, 240, 0.2) 0%, transparent 60%)',
              }}
            />

            {/* Nature scene fading/blurring */}
            <motion.div 
              className="absolute inset-0 z-5"
              animate={{ 
                opacity: reversePhase >= 3 ? 0.3 : reversePhase >= 2 ? 0.6 : 1,
                filter: reversePhase >= 3 ? 'blur(12px)' : reversePhase >= 2 ? 'blur(6px)' : 'blur(0px)',
              }}
              transition={{ duration: 1 }}
            />
          </>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// Post Experience Screen
// Post Experience - Soft continuation, NOT a hard UI cut
const PostExperienceScreen = ({ onSave, onViewVisions, onSkip, saving, saveState, saveError, selectedImages, visionName, setVisionName }) => {
  const [showActions, setShowActions] = useState(false);
  
  useEffect(() => {
    const timer = setTimeout(() => setShowActions(true), 1200);
    return () => clearTimeout(timer);
  }, []);

  const bgUrl = 'https://images.pexels.com/photos/23499343/pexels-photo-23499343.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

  return (
    <motion.div 
      className="fixed inset-0 flex flex-col items-center justify-center px-8"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 1.2, ease: 'easeOut' }}
      data-testid="post-experience-screen"
    >
      {/* Cinematic background */}
      <div className="absolute inset-0 z-0">
        <img src={bgUrl} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0" style={{ background: 'linear-gradient(180deg, rgba(10,15,20,0.4) 0%, rgba(10,15,20,0.7) 50%, rgba(10,15,20,0.92) 100%)' }} />
      </div>

      {/* Soft breathing glow */}
      <motion.div 
        className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[400px] rounded-full pointer-events-none z-[1]"
        animate={{ opacity: [0.06, 0.12, 0.06], scale: [0.95, 1.05, 0.95] }}
        transition={{ duration: 8, repeat: Infinity, ease: 'easeInOut' }}
        style={{ background: 'radial-gradient(ellipse, rgba(180, 160, 120, 0.15) 0%, transparent 60%)' }}
      />

      <motion.div 
        className="relative z-10 text-center max-w-lg w-full"
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4, duration: 0.8 }}
      >
        {/* Saved state: show board preview */}
        {saveState === 'saved' ? (
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8 }}
          >
            {/* Mini board preview — every unique image */}
            <div className="relative w-full aspect-[4/3] rounded-2xl overflow-hidden mb-8 mx-auto max-w-sm" style={{
              boxShadow: '0 20px 60px rgba(0,0,0,0.4), 0 0 40px rgba(180, 160, 120, 0.08)',
            }}>
              {(() => {
                const seen = new Set();
                const uniques = [];
                for (const img of selectedImages || []) {
                  if (!img?.url || seen.has(img.url)) continue;
                  seen.add(img.url);
                  uniques.push(img);
                }
                const cols = uniques.length <= 4 ? 2 : uniques.length <= 9 ? 3 : 4;
                return (
                  <div
                    className="absolute inset-0 grid gap-0.5"
                    style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))` }}
                  >
                    {uniques.map((img, i) => (
                      <img key={img.url || i} src={img.url} alt="" className="w-full h-full object-cover" />
                    ))}
                  </div>
                );
              })()}
              <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent" />
            </div>
            
            <motion.div
              className="w-12 h-12 rounded-full mx-auto mb-4 flex items-center justify-center"
              style={{ background: 'rgba(120, 160, 100, 0.25)', border: '1px solid rgba(120, 160, 100, 0.3)' }}
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', delay: 0.3 }}
            >
              <Check size={22} className="text-[#a8d090]" />
            </motion.div>

            <h1 className="text-2xl text-[#FAF9F6]/90 mb-2" style={{ fontFamily: "'Playfair Display', serif", fontStyle: 'italic' }}>
              It is done.
            </h1>
            <p className="text-white/35 text-sm mb-8" style={{ fontFamily: "'Manrope', sans-serif" }}>
              Your vision is now part of you
            </p>

            <motion.button
              onClick={onViewVisions}
              className="w-full py-4 px-8 rounded-full text-base font-medium text-white/90 flex items-center justify-center gap-3"
              style={{
                fontFamily: "'Manrope', sans-serif",
                background: 'rgba(255, 255, 255, 0.08)',
                backdropFilter: 'blur(20px)',
                border: '1px solid rgba(255, 255, 255, 0.15)',
              }}
              whileHover={{ background: 'rgba(255, 255, 255, 0.14)' }}
              whileTap={{ scale: 0.98 }}
              data-testid="view-immersions-btn"
            >
              <Eye size={17} /> View My Visions
            </motion.button>
          </motion.div>
        ) : (
          <>
            <motion.h1 
              className="text-3xl md:text-4xl text-[#FAF9F6]/95 mb-4 leading-relaxed"
              style={{ fontFamily: "'Playfair Display', serif", fontWeight: 400, fontStyle: 'italic', letterSpacing: '0.02em' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.6, duration: 1 }}
            >
              It is done.
            </motion.h1>
            
            <motion.p 
              className="text-white/35 text-base mb-12"
              style={{ fontFamily: "'Manrope', sans-serif", letterSpacing: '0.03em' }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.9, duration: 0.8 }}
            >
              Your vision is now part of you.
            </motion.p>

            <motion.div 
              className="space-y-4"
              initial={{ opacity: 0, y: 15 }}
              animate={{ opacity: showActions ? 1 : 0, y: showActions ? 0 : 15 }}
              transition={{ duration: 0.7 }}
              style={{ pointerEvents: showActions ? 'auto' : 'none' }}
            >
              {/* Name your vision */}
              <motion.div
                className="w-full mb-4"
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: showActions ? 1 : 0, y: showActions ? 0 : 10 }}
                transition={{ duration: 0.5, delay: 0.2 }}
              >
                <input
                  type="text"
                  value={visionName}
                  onChange={(e) => setVisionName(e.target.value)}
                  placeholder="Name this vision..."
                  className="w-full py-3 px-5 rounded-full text-sm text-white/80 placeholder-white/25 outline-none"
                  style={{
                    fontFamily: "'Playfair Display', serif",
                    fontStyle: 'italic',
                    background: 'rgba(255, 255, 255, 0.05)',
                    border: '1px solid rgba(255, 255, 255, 0.1)',
                    backdropFilter: 'blur(12px)',
                    letterSpacing: '0.03em',
                  }}
                  data-testid="vision-name-input"
                />
              </motion.div>

              {/* Primary: Save to My Visions */}
              <motion.button
                onClick={onSave}
                disabled={saving}
                className="w-full py-4 px-8 rounded-full text-base font-medium text-white/90 flex items-center justify-center gap-3"
                style={{ 
                  fontFamily: "'Manrope', sans-serif",
                  background: saving ? 'rgba(255, 255, 255, 0.04)' : 'rgba(255, 255, 255, 0.08)',
                  backdropFilter: 'blur(20px)',
                  border: saving ? '1px solid rgba(255, 255, 255, 0.08)' : '1px solid rgba(255, 255, 255, 0.18)',
                  boxShadow: '0 8px 32px rgba(0, 0, 0, 0.15)',
                  letterSpacing: '0.02em',
                }}
                whileHover={!saving ? { background: 'rgba(255, 255, 255, 0.14)', boxShadow: '0 8px 40px rgba(0, 0, 0, 0.2)' } : {}}
                whileTap={!saving ? { scale: 0.98 } : {}}
                data-testid="save-immersion-btn"
              >
                {saving ? (
                  <motion.span animate={{ opacity: [0.5, 1, 0.5] }} transition={{ duration: 1.5, repeat: Infinity }}>Saving...</motion.span>
                ) : (
                  <><Heart size={17} /> Save to My Visions</>
                )}
              </motion.button>

              {saveState === 'error' && (
                <motion.div 
                  className="w-full py-3 px-5 rounded-xl text-center"
                  style={{ 
                    fontFamily: "'Manrope', sans-serif",
                    background: 'rgba(220, 80, 60, 0.15)',
                    border: '1px solid rgba(220, 80, 60, 0.25)',
                  }}
                  initial={{ opacity: 0, y: 5 }}
                  animate={{ opacity: 1, y: 0 }}
                  data-testid="save-error-message"
                >
                  <p className="text-white/70 text-sm">
                    {saveError || "Couldn't save right now."}
                  </p>
                  <p className="text-white/40 text-xs mt-1">Tap "Save" to try again.</p>
                </motion.div>
              )}

              {/* Secondary: View My Visions */}
              <motion.button
                onClick={!saving ? onViewVisions : undefined}
                disabled={saving}
                className="w-full py-4 px-8 rounded-full text-base font-medium text-white/50 flex items-center justify-center gap-3"
                style={{ 
                  fontFamily: "'Manrope', sans-serif",
                  background: 'rgba(255, 255, 255, 0.03)',
                  backdropFilter: 'blur(12px)',
                  border: '1px solid rgba(255, 255, 255, 0.08)',
                  opacity: saving ? 0.4 : 1,
                }}
                whileHover={!saving ? { background: 'rgba(255, 255, 255, 0.07)' } : {}}
                whileTap={!saving ? { scale: 0.98 } : {}}
                data-testid="view-visions-btn"
              >
                <Eye size={17} /> View My Visions
              </motion.button>

              {/* Tertiary: Set Daily Reminder */}
              <motion.button
                disabled={saving}
                className="w-full py-4 px-8 rounded-full text-base font-medium text-white/40 flex items-center justify-center gap-3"
                style={{ 
                  fontFamily: "'Manrope', sans-serif",
                  background: 'transparent',
                  border: '1px solid rgba(255, 255, 255, 0.05)',
                  opacity: saving ? 0.3 : 1,
                }}
                whileHover={!saving ? { background: 'rgba(255, 255, 255, 0.03)', borderColor: 'rgba(255,255,255,0.1)' } : {}}
                data-testid="enable-reminder-btn"
              >
                <Bell size={17} /> Set Daily Reminder
              </motion.button>

              {/* Not now — skips without saving */}
              <motion.button
                onClick={!saving ? onSkip : undefined}
                disabled={saving}
                className="text-white/25 text-sm mt-6 hover:text-white/40 transition-colors duration-300"
                style={{ fontFamily: "'Manrope', sans-serif", letterSpacing: '0.03em', opacity: saving ? 0.3 : 1 }}
                data-testid="not-now-btn"
              >
                Not now
              </motion.button>
            </motion.div>
          </>
        )}
      </motion.div>
    </motion.div>
  );
};

// ============================================
// MAIN COMPONENT
// ============================================

const LivingVisionBoard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  // Foundation video for the cinematic immersion. Aurum and Reclaim main
  // immersion share the same bespoke piece, mobile vs desktop variant
  // resolved once at mount.
  const foundationVideoUrl = useFoundationVideo();
  
  // Check if we're returning from gallery with saved immersion data
  const savedImmersion = location.state?.savedImmersion;
  const startAtStep = location.state?.startAtStep;
  
  // Clear state after reading to prevent re-trigger on back navigation
  useEffect(() => {
    if (savedImmersion) {
      window.history.replaceState({}, document.title);
    }
  }, [savedImmersion]);
  
  const [step, setStep] = useState(startAtStep || 0);
  const [categoryIndex, setCategoryIndex] = useState(0);
  const [selectedImages, setSelectedImages] = useState(
    savedImmersion?.selected_images || []
  );
  const [selectedWords, setSelectedWords] = useState(
    savedImmersion?.selected_words || []
  );
  const [personalImages, setPersonalImages] = useState([]);
  const [immersionType, setImmersionType] = useState('still'); // 'still' | 'video'
  const [selectedAffirmations, setSelectedAffirmations] = useState(
    savedImmersion?.selected_affirmations || []
  );
  const [isPremium] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveState, setSaveState] = useState('idle'); // idle | saving | saved | error
  
  // Favorites state
  const [favorites, setFavorites] = useState([]);
  const [loadingFavorites, setLoadingFavorites] = useState(true);
  
  // Layout seed for consistent collage recreation
  const [layoutSeed] = useState(() => savedImmersion?.layout_seed || Math.random());

  // AI Image Personalisation — track every selection/deselection
  const { track: trackImage, rank: rankImages } = useImagePersonalisation();

  // Phase 2: per-category personalised ordering. Cache keyed by category id.
  const [personalisedOrder, setPersonalisedOrder] = useState({});
  const [personalisationActive, setPersonalisationActive] = useState(false);
  
  // Get user ID — uses authenticated email when logged in (so visions are
  // visible across devices); falls back to anon localStorage id for guests.
  const userId = useResolvedUserId();
  
  // Load favorites on mount
  useEffect(() => {
    const loadFavorites = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/vision-board/favorites/${userId}`);
        if (response.ok) {
          const data = await response.json();
          setFavorites(data.favorites || []);
        }
      } catch (error) {
        console.error('Failed to load favorites:', error);
      } finally {
        setLoadingFavorites(false);
      }
    };
    loadFavorites();
  }, [userId]);

  // Load saved visions for entry screen
  const [savedVisions, setSavedVisions] = useState([]);
  useEffect(() => {
    const loadSavedVisions = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/vision-board/immersions/${userId}`);
        if (response.ok) {
          const data = await response.json();
          setSavedVisions(data.immersions || []);
        }
      } catch (error) {
        console.log('No saved visions found');
      }
    };
    loadSavedVisions();
  }, [userId]);

  // Load admin-added custom images and merge with hardcoded
  const [customImages, setCustomImages] = useState({});
  useEffect(() => {
    const loadCustomImages = async () => {
      try {
        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/vision-board/admin/images`);
        if (response.ok) {
          const data = await response.json();
          setCustomImages(data.images || {});
        }
      } catch (error) {
        console.log('No custom images loaded');
      }
    };
    loadCustomImages();
  }, []);

  // Merged category images: hardcoded + admin-added
  const mergedCategoryImages = useMemo(() => {
    const merged = {};
    for (const cat of CATEGORIES) {
      const hardcoded = CATEGORY_IMAGES[cat.id] || [];
      const custom = (customImages[cat.id] || []).map(img => ({ url: img.url }));
      merged[cat.id] = [...hardcoded, ...custom];
    }
    return merged;
  }, [customImages]);

  // Phase 2: when the user lands on a category, re-rank that category's
  // images using the user's emotional-tone profile (backed by /rank).
  useEffect(() => {
    const cat = CATEGORIES[categoryIndex];
    if (!cat) return;
    const base = mergedCategoryImages[cat.id] || [];
    if (!base.length) return;
    if (personalisedOrder[cat.id]?._sourceLength === base.length) return; // already ranked

    let cancelled = false;
    rankImages(base, cat.id).then((ranked) => {
      if (cancelled) return;
      // /rank returns an array of full image objects when personalised, original otherwise.
      const sameOrder = ranked.every((img, i) => (img.url || img.id) === (base[i]?.url || base[i]?.id));
      setPersonalisedOrder((prev) => ({
        ...prev,
        [cat.id]: { items: ranked, _sourceLength: base.length, applied: !sameOrder },
      }));
      if (!sameOrder) setPersonalisationActive(true);
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryIndex, mergedCategoryImages]);

  const visibleCategoryImages = useMemo(() => {
    const cat = CATEGORIES[categoryIndex];
    const base = mergedCategoryImages[cat?.id] || [];
    const ranked = personalisedOrder[cat?.id]?.items;
    return ranked && ranked.length === base.length ? ranked : base;
  }, [categoryIndex, mergedCategoryImages, personalisedOrder]);
  
  // Toggle favorite
  const handleToggleFavorite = async (image, category) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/vision-board/favorites/toggle`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: userId,
          image_url: image.url,
          category: category
        })
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.favorited) {
          setFavorites(prev => [...prev, { image_url: image.url, category }]);
        } else {
          setFavorites(prev => prev.filter(f => f.image_url !== image.url));
        }
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error);
    }
  };
  
  // Check if image is favorited
  const isFavorited = (imageUrl) => {
    return favorites.some(f => f.image_url === imageUrl);
  };

  // Get all available affirmations
  const allAffirmations = useMemo(() => {
    return [
      ...IMMERSION_AFFIRMATIONS.core,
      ...IMMERSION_AFFIRMATIONS.deepening,
      ...IMMERSION_AFFIRMATIONS.surrender,
      ...IMMERSION_AFFIRMATIONS.identity,
      ...IMMERSION_AFFIRMATIONS.closing,
    ];
  }, []);

  // Pre-select first 12 affirmations by default (only if not loaded from saved immersion)
  useEffect(() => {
    if (selectedAffirmations.length === 0 && !savedImmersion) {
      setSelectedAffirmations(allAffirmations.slice(0, 12));
    }
  }, [allAffirmations, selectedAffirmations.length, savedImmersion]);

  const handleAffirmationToggle = (affirmation) => {
    setSelectedAffirmations(prev => {
      if (prev.includes(affirmation)) {
        return prev.filter(a => a !== affirmation);
      }
      return [...prev, affirmation];
    });
  };

  const handleImageSelect = (image) => {
    const categoryId = CATEGORIES[categoryIndex].id;
    setSelectedImages(prev => {
      const exists = prev.some(img => img.url === image.url);
      if (exists) {
        // Deselection — track as a soft "skip"
        try { trackImage({ ...image, category: categoryId }, "skip", "vision_board"); } catch {}
        return prev.filter(img => img.url !== image.url);
      }
      if (prev.length >= 50) return prev;
      // Selection — track for personalisation
      try { trackImage({ ...image, category: categoryId }, "select", "vision_board"); } catch {}
      return [...prev, { ...image, category: categoryId }];
    });
  };

  const handleWordSelect = (word) => {
    setSelectedWords(prev => {
      if (prev.includes(word)) {
        return prev.filter(w => w !== word);
      }
      if (prev.length >= 50) return prev;
      return [...prev, word];
    });
  };

  const handlePersonalUpload = (image) => {
    setPersonalImages(prev => [...prev, image]);
    setSelectedImages(prev => [...prev, image]);
  };

  const handleNextCategory = () => {
    if (categoryIndex < CATEGORIES.length - 1) {
      setCategoryIndex(prev => prev + 1);
    } else {
      setStep(2);
    }
  };

  const handlePrevCategory = () => {
    if (categoryIndex > 0) {
      setCategoryIndex(prev => prev - 1);
    } else {
      setStep(0);
    }
  };

  const [saveError, setSaveError] = useState('');
  const [visionName, setVisionName] = useState('');
  
  const handleSave = async () => {
    if (saving || saveState === 'saved') return;
    setSaving(true);
    setSaveState('saving');
    setSaveError('');
    
    try {
      // Build lean payload — filter out data: URLs from personal images (too large)
      const cleanImages = (selectedImages || []).slice(0, 30).map(img => {
        const url = String(img.url || '');
        return {
          url: url.startsWith('data:') ? '' : url.substring(0, 500),
          category: String(img.category || 'unknown'),
          isPersonal: Boolean(img.isPersonal),
        };
      }).filter(img => img.url.length > 0);

      const payload = {
        user_id: userId || 'anonymous',
        layout_seed: layoutSeed || 0,
        name: visionName.trim() || undefined,
        selected_images: cleanImages,
        selected_words: (selectedWords || []).slice(0, 20).map(w => String(w)),
        selected_affirmations: (selectedAffirmations || []).slice(0, 20).map(a => String(a)),
      };

      const apiUrl = `${process.env.REACT_APP_BACKEND_URL}/api/vision-board/immersions`;
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 20000);

      const response = await fetch(apiUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (response.ok) {
        setSaveState('saved');
      } else {
        const errText = await response.text().catch(() => 'Unknown error');
        console.error('[Save] Server error:', response.status, errText);
        setSaveState('error');
        setSaveError(`Save failed (${response.status})`);
      }
    } catch (err) {
      console.error('[Save] Network error:', err.name, err.message);
      setSaveState('error');
      setSaveError(err.name === 'AbortError' ? 'Request timed out' : 'Network error — check your connection');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0" style={{ background: '#060a07' }}>
      <AnimatePresence mode="wait">
        {step === 0 && (
          <EntryScreen
            key="entry"
            onStart={() => setStep(0.5)}
            onCreatePerformance={() => navigate('/performance-vision')}
            savedVisions={savedVisions}
          />
        )}

        {step === 0.5 && (
          <ImageSourceChoice
            key="source-choice"
            onBack={() => setStep(0)}
            onChooseReclaim={() => setStep(1)}
            onChooseOwn={() => setStep(1)}  // For now both flow to Step 1; upload layer ships Monday
          />
        )}

        {step === 1 && (
          <>
            <CategoryProgressBar 
              categories={CATEGORIES} 
              currentIndex={categoryIndex} 
            />
            <ImageSelectionScreen
              key={`category-${categoryIndex}`}
              category={CATEGORIES[categoryIndex]}
              images={visibleCategoryImages}
              personalised={personalisationActive}
              selectedImages={selectedImages}
              onSelect={handleImageSelect}
              onNext={handleNextCategory}
              onBack={handlePrevCategory}
              totalSelected={selectedImages.length}
              categoryIndex={categoryIndex}
              totalCategories={CATEGORIES.length}
              favorites={favorites}
              onToggleFavorite={handleToggleFavorite}
              isFavorited={isFavorited}
            />
          </>
        )}

        {step === 2 && (
          <WordSelectionScreen
            key="words"
            selectedWords={selectedWords}
            onSelect={handleWordSelect}
            onNext={() => setStep(3)}
            onBack={() => { setStep(1); setCategoryIndex(CATEGORIES.length - 1); }}
          />
        )}

        {step === 3 && (
          <PersonalLibraryScreen
            key="library"
            personalImages={personalImages}
            onUpload={handlePersonalUpload}
            onNext={() => setStep(4)}
            onBack={() => setStep(2)}
            isPremium={isPremium}
          />
        )}

        {step === 4 && (
          <RevealScreen
            key="reveal"
            selectedImages={selectedImages}
            selectedWords={selectedWords}
            onEnterImmersion={() => setStep(5)}
            onBack={() => setStep(3)}
            layoutSeed={layoutSeed}
          />
        )}

        {step === 5 && (
          <AffirmationSelectionScreen
            key="affirmations"
            selectedAffirmations={selectedAffirmations}
            onToggle={handleAffirmationToggle}
            onNext={(type) => { if (type) setImmersionType(type); setStep(6); }}
            onBack={() => setStep(4)}
            availableAffirmations={allAffirmations}
            showImmersionChoice={true}
          />
        )}

        {step === 6 && immersionType === 'still' && (
          <ImmersionScreen
            key="immersion"
            selectedImages={selectedImages}
            selectedWords={selectedWords}
            selectedAffirmations={selectedAffirmations}
            onComplete={() => setStep(7)}
            onViewBoard={() => setStep(4)}
          />
        )}

        {step === 6 && immersionType === 'video' && (
          <VideoImmersionScreen
            key="video-immersion"
            selectedWords={selectedWords}
            selectedAffirmations={selectedAffirmations}
            selectedImages={selectedImages}
            onComplete={() => setStep(7)}
            onViewBoard={() => setStep(4)}
            foundationVideoUrl={foundationVideoUrl}
          />
        )}

        {step === 7 && (
          <PostExperienceScreen
            key="post"
            onSave={handleSave}
            onViewVisions={() => navigate('/abundance/my-visions')}
            onSkip={() => navigate('/abundance')}
            saving={saving}
            saveState={saveState}
            saveError={saveError}
            selectedImages={selectedImages}
            visionName={visionName}
            setVisionName={setVisionName}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default LivingVisionBoard;
